import DateFormat from "sap/ui/core/format/DateFormat";
import Localization from "sap/base/i18n/Localization";
import { lookupCountryName } from "./countries";

/** Order of the i18n label parts expected by formatUserType — keep in sync with the views. */
const USER_TYPE_VALUES = [
	"ComputecAdmin", "ComputecAuditor", "ComputecMarketing", "ComputecUser", "ComputecDeveloper",
	"PartnerAdmin", "PartnerUser",
	"CustomerAdmin", "CustomerUser", "CustomerLicenseOnly",
	"System",
	"Unknown"
] as const;

/** Order of the i18n label parts expected by formatUserStatus — keep in sync with the views. */
const USER_STATUS_VALUES = ["Pending", "Active", "Disabled", "Removed"] as const;

/** Label at the position of `value` in `values`; the raw value when it is not listed or its label is missing. */
function labelFor(values: readonly string[], value: string | null | undefined, labels: (string | null | undefined)[]): string {
	if (!value) {
		return "";
	}
	const idx = values.indexOf(value);
	return idx < 0 ? value : (labels[idx] ?? value);
}

const formatter = {
	/**
	 * Identity formatter for OData v4 enum properties — sap.m.Text/ObjectStatus do not display
	 * untyped enum values out of the box, so a formatter forces the value through to the DOM.
	 */
	formatEnum(value: string | number | null | undefined): string {
		return value == null ? "" : String(value);
	},

	formatDate(value: string | Date | null | undefined): string {
		if (!value) {
			return "";
		}
		const d = value instanceof Date ? value : new Date(value);
		if (Number.isNaN(d.getTime())) {
			return "";
		}
		const fmt = DateFormat.getDateTimeInstance({ style: "medium" });
		return fmt.format(d);
	},

	/**
	 * Relative time ("2 hours ago", "5 minutes ago"). Używane na Dashboardzie do timeline'a
	 * Recent Activity — krótszy zapis niż pełna data, łatwiejszy do odczytu na karcie.
	 */
	formatRelativeTime(value: string | Date | null | undefined): string {
		if (!value) {
			return "";
		}
		const d = value instanceof Date ? value : new Date(value);
		if (Number.isNaN(d.getTime())) {
			return "";
		}
		const lang = Localization.getLanguage();
		const diffMs = Date.now() - d.getTime();
		const minutes = Math.round(diffMs / 60000);
		// Intl.RelativeTimeFormat obsługuje EN i PL (i każdy inny język) automatycznie.
		const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
		if (minutes < 60) return rtf.format(-minutes, "minute");
		const hours = Math.round(minutes / 60);
		if (hours < 24) return rtf.format(-hours, "hour");
		const days = Math.round(hours / 24);
		if (days < 7) return rtf.format(-days, "day");
		const fmt = DateFormat.getDateInstance({ style: "medium" });
		return fmt.format(d);
	},

	/**
	 * Wybiera ikonę dla wpisu audit log na podstawie ChangeType (Command/DataMutation/DomainEvent).
	 */
	formatAuditIcon(changeType: string | undefined): string {
		switch (changeType) {
			case "Command":      return "sap-icon://action";
			case "DataMutation": return "sap-icon://edit";
			case "DomainEvent":  return "sap-icon://activity-2";
			default: return "sap-icon://history";
		}
	},

	/**
	 * Status state dla flagi IsActive na osobie kontaktowej.
	 * Powód użycia formattera zamiast expression binding `{= ${...} ? ... : ... }` —
	 * w sap.m.Table z OData v4 expression bindings nie zawsze są re-evaluated, gdy
	 * pojedyncze property się zmienia po `model.refresh()` (cache key-based identyfikuje
	 * wiersz po Id, ale expression w cell template trzyma stary wynik).
	 */
	formatActiveState(isActive: boolean | null | undefined): string {
		return isActive ? "Success" : "None";
	},

	formatOrgTypeState(type: string | undefined): string {
		switch (type) {
			case "CompuTec": return "Information";
			case "Partner":  return "Success";
			case "EndCustomer": return "None";
			default: return "None";
		}
	},

	/**
	 * Lokalizowana etykieta typu organizacji. Przyjmuje raw enum value + 3 stringi z i18n
	 * przez parts-binding (pre-resolved przez framework przed wywołaniem formattera).
	 *
	 * Wzorzec: nie używamy controller.method bo Type renderuje się w wielu miejscach
	 * (header ObjectStatus, basics SimpleForm, lista organizacji) — static formatter +
	 * parts binding utrzymuje dystrybucję logiki w jednym miejscu, bez duplikatów per
	 * controller. Patrz długi komentarz w `OrganizationDetail.controller.ts` przy
	 * `formatUserRole` o ograniczeniach formatterów (brak DI do bundle) — parts binding
	 * to zalecany workaround dopóki nie wprowadzimy bundle event-chain helpera.
	 *
	 * @example
	 * <Text text="{
	 *     parts: [
	 *         {path: 'odata>Type', targetType: 'any'},
	 *         {path: 'i18n>organization.type.CompuTec'},
	 *         {path: 'i18n>organization.type.Partner'},
	 *         {path: 'i18n>organization.type.EndCustomer'}
	 *     ],
	 *     formatter: 'Formatter.formatOrgType'
	 * }"/>
	 */
	formatOrgType(
		type: string | null | undefined,
		labelCompuTec: string | null | undefined,
		labelPartner: string | null | undefined,
		labelEndCustomer: string | null | undefined
	): string {
		switch (type) {
			case "CompuTec":   return labelCompuTec ?? type;
			case "Partner":    return labelPartner ?? type;
			case "EndCustomer": return labelEndCustomer ?? type;
			default: return type ?? "";
		}
	},

	/**
	 * Wyświetla zlokalizowaną nazwę kraju z ISO 3166-1 alpha-2 (np. "PL" → "Polska").
	 * Dla legacy danych (raw "Polska", "Poland") — passthrough bez zmiany. Patrz
	 * {@link computec.b2b.portal.model.lookupCountryName}.
	 */
	formatCountry(code: string | null | undefined): string {
		return lookupCountryName(code);
	},

	formatOrgStatusState(status: string | undefined): string {
		switch (status) {
			case "Active":   return "Success";
			case "Pending":  return "Warning";
			case "Inactive": return "Error";
			default: return "None";
		}
	},

	/**
	 * Localized label of the synthesized UserType. The labels arrive through a parts binding,
	 * one `users.type.<value>` key per entry of USER_TYPE_VALUES, in that order. A value outside
	 * the list is shown raw.
	 *
	 * @example
	 * <Text text="{
	 *     parts: [
	 *         {path: 'odata>UserType', targetType: 'any'},
	 *         {path: 'i18n>users.type.ComputecAdmin'},
	 *         {path: 'i18n>users.type.ComputecAuditor'},
	 *         {path: 'i18n>users.type.ComputecMarketing'},
	 *         {path: 'i18n>users.type.ComputecUser'},
	 *         {path: 'i18n>users.type.ComputecDeveloper'},
	 *         {path: 'i18n>users.type.PartnerAdmin'},
	 *         {path: 'i18n>users.type.PartnerUser'},
	 *         {path: 'i18n>users.type.CustomerAdmin'},
	 *         {path: 'i18n>users.type.CustomerUser'},
	 *         {path: 'i18n>users.type.CustomerLicenseOnly'},
	 *         {path: 'i18n>users.type.System'},
	 *         {path: 'i18n>users.type.Unknown'}
	 *     ],
	 *     formatter: 'Formatter.formatUserType'
	 * }"/>
	 */
	formatUserType(value: string | null | undefined, ...labels: (string | null | undefined)[]): string {
		return labelFor(USER_TYPE_VALUES, value, labels);
	},

	/**
	 * Localized label of the user status. The labels arrive through a parts binding, one
	 * `users.status.<value>` key per entry of USER_STATUS_VALUES, in that order.
	 *
	 * @example
	 * <ObjectStatus text="{
	 *     parts: [
	 *         {path: 'odata>Status', targetType: 'any'},
	 *         {path: 'i18n>users.status.Pending'},
	 *         {path: 'i18n>users.status.Active'},
	 *         {path: 'i18n>users.status.Disabled'},
	 *         {path: 'i18n>users.status.Removed'}
	 *     ],
	 *     formatter: 'Formatter.formatUserStatus'
	 * }"/>
	 */
	formatUserStatus(status: string | null | undefined, ...labels: (string | null | undefined)[]): string {
		return labelFor(USER_STATUS_VALUES, status, labels);
	},

	formatUserStatusState(status: string | undefined): string {
		switch (status) {
			case "Active":   return "Success";
			case "Pending":  return "Warning";
			case "Disabled": return "Error";
			case "Removed":  return "None";
			default: return "None";
		}
	},

	formatUserStatusIcon(status: string | undefined): string {
		return status === "Pending" ? "sap-icon://email" : "";
	},

	/**
	 * Access badge — Portal user vs License-only — na liście Portal Users.
	 * Parts binding: HasPortalAccess + 2 etykiety i18n (jak <see cref="formatOrgType"/>).
	 */
	formatAccessText(
		hasPortalAccess: boolean | null | undefined,
		labelPortal: string | null | undefined,
		labelLicenseOnly: string | null | undefined
	): string {
		return hasPortalAccess ? (labelPortal ?? "Portal user") : (labelLicenseOnly ?? "License only");
	},

	formatAccessState(hasPortalAccess: boolean | null | undefined): string {
		return hasPortalAccess ? "Information" : "None";
	},

	/**
	 * Tooltip dla statusu Pending — pokazuje datę wygaśnięcia zaproszenia.
	 * Pusty string gdy status != Pending lub brak daty (ObjectStatus.tooltip="" jest no-op).
	 */
	formatPendingExpiry(
		status: string | null | undefined,
		expiresAt: string | Date | null | undefined,
		labelPrefix: string | null | undefined
	): string {
		if (status !== "Pending" || !expiresAt) {
			return "";
		}
		const d = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);
		if (Number.isNaN(d.getTime())) {
			return "";
		}
		const fmt = DateFormat.getDateTimeInstance({ style: "medium" });
		return `${labelPrefix ?? "Invitation expires"}: ${fmt.format(d)}`;
	},

	/**
	 * Superseded-key anomaly banner text (ADR-024 Task 5). Formats the LR-recorded anomaly
	 * (a previously superseded environment presented a retired key — possible clone) into a
	 * localized warning line, interpolating the detection timestamp and (when present) the
	 * presented key fingerprint.
	 *
	 * Parts binding — the i18n templates are pre-resolved by the framework and passed in, so the
	 * static formatter never needs to reach for the resource bundle itself (same pattern as
	 * {@link formatOrgType} / {@link formatPendingExpiry}). The template strings use {0} for the
	 * detected-at date and {1} for the fingerprint.
	 *
	 * @example
	 * <MessageStrip text="{
	 *     parts: [
	 *         {path: 'odata>SupersededKeyAnomalyDetectedAt'},
	 *         {path: 'odata>SupersededKeyAnomalyFingerprint'},
	 *         {path: 'i18n>deploymentDetail.anomaly.supersededKey'},
	 *         {path: 'i18n>deploymentDetail.anomaly.supersededKey.withFingerprint'}
	 *     ],
	 *     formatter: 'Formatter.formatSupersededKeyAnomaly'
	 * }"/>
	 */
	formatSupersededKeyAnomaly(
		detectedAt: string | Date | null | undefined,
		fingerprint: string | null | undefined,
		template: string | null | undefined,
		templateWithFingerprint: string | null | undefined
	): string {
		if (!detectedAt) {
			return "";
		}
		const d = detectedAt instanceof Date ? detectedAt : new Date(detectedAt);
		if (Number.isNaN(d.getTime())) {
			return "";
		}
		const when = DateFormat.getDateTimeInstance({ style: "medium" }).format(d);
		if (fingerprint) {
			const tpl = templateWithFingerprint
				?? "A superseded environment attempted to authenticate — possible clone; detected at {0} (key {1}).";
			return tpl.replace("{0}", when).replace("{1}", fingerprint);
		}
		const tplNoFp = template
			?? "A superseded environment attempted to authenticate — possible clone; detected at {0}.";
		return tplNoFp.replace("{0}", when);
	},

	formatProductStatusState(status: string | undefined): string {
		switch (status) {
			case "Active":     return "Success";
			case "Inactive":   return "Warning";
			case "Deprecated": return "Error";
			case "Removed":    return "Error";
			default: return "None";
		}
	},

	formatOrderStatusState(status: string | undefined): string {
		switch (status) {
			case "Approved":  return "Information";
			case "Fulfilled": return "Success";
			case "Placed":    return "Warning";
			case "Voided":    return "Error";
			default: return "None";
		}
	},

	formatOrderPaymentStatusState(status: string | undefined): string {
		switch (status) {
			case "Paid":            return "Success";
			case "Unpaid":          return "Warning";
			case "PaymentOverdue":  return "Error";
			default: return "None";
		}
	},

	formatEntitlementStatusState(status: string | undefined): string {
		switch (status) {
			case "Active":     return "Success";
			case "Pending":    return "Warning";
			case "Suspended":  return "Error";
			case "Cancelled":  return "Error";
			case "Expired":    return "None";
			default: return "None";
		}
	},

	formatEntitlementPaymentStateState(state: string | undefined): string {
		switch (state) {
			case "Paid":            return "Success";
			case "GracePeriod":     return "Warning";
			case "PendingPayment":  return "Warning";
			case "Overdue":         return "Error";
			case "NotRequired":     return "None";
			default: return "None";
		}
	},

	/**
	 * Formats GraceUntil as "N days left" (warning) / "expired" (error) / "—" (no grace).
	 * Used in Entitlements table; UI shows just text — caller wraps with ObjectStatus
	 * if state coloring needed.
	 */
	formatGraceCountdown(value: string | Date | null | undefined): string {
		if (!value) {
			return "—";
		}
		const d = value instanceof Date ? value : new Date(value);
		if (Number.isNaN(d.getTime())) {
			return "—";
		}
		const diffMs = d.getTime() - Date.now();
		const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
		if (days < 0) {
			return `expired ${Math.abs(days)}d ago`;
		}
		if (days === 0) {
			return "ends today";
		}
		return `${days}d left`;
	},

	formatBool(value: boolean | string | null | undefined): string {
		if (value === true || value === "true") return "✓";
		if (value === false || value === "false") return "✗";
		return "";
	},

	formatLicenseStatusState(status: string | undefined): string {
		switch (status) {
			case "Active":    return "Success";
			case "Suspended": return "Warning";
			case "Voided":    return "Error";
			case "Unbound":   return "None";
			case "Expired":   return "None";
			default: return "None";
		}
	},

	formatDeploymentStatusState(status: string | undefined): string {
		switch (status) {
			case "Active":             return "Success";
			case "PendingEnrollment":  return "Warning";
			case "Decommissioned":     return "None";
			case "EnrollmentExpired":  return "Warning";
			default: return "None";
		}
	},

	formatOrgStatusColorScheme(status: string | undefined): number {
		switch (status) {
			case "Active":            return 8;
			case "PendingAcceptance": return 1;
			case "Suspended":         return 2;
			case "Rejected":          return 2;
			default: return 10;
		}
	},

	formatOrgTypeColorScheme(type: string | undefined): number {
		switch (type) {
			case "CompuTec":    return 6;
			case "Partner":     return 8;
			case "EndCustomer": return 10;
			default: return 10;
		}
	},

	formatUserStatusColorScheme(status: string | undefined): number {
		switch (status) {
			case "Active":   return 8;
			case "Pending":  return 1;
			case "Disabled": return 10;
			case "Removed":  return 2;
			default: return 10;
		}
	},

	formatAccessColorScheme(hasPortalAccess: boolean | null | undefined): number {
		return hasPortalAccess ? 6 : 10;
	},

	formatProductStatusColorScheme(status: string | undefined): number {
		switch (status) {
			case "Active":     return 8;
			case "Inactive":   return 10;
			case "Deprecated": return 2;
			case "Removed":    return 2;
			default: return 10;
		}
	},

	formatOrderStatusColorScheme(status: string | undefined): number {
		switch (status) {
			case "Approved":  return 6;
			case "Fulfilled": return 8;
			case "Placed":    return 1;
			case "Voided":    return 2;
			default: return 10;
		}
	},

	formatOrderPaymentStatusColorScheme(status: string | undefined): number {
		switch (status) {
			case "Paid":           return 8;
			case "Unpaid":         return 1;
			case "PaymentOverdue": return 2;
			default: return 10;
		}
	},

	formatEntitlementStatusColorScheme(status: string | undefined): number {
		switch (status) {
			case "Active":    return 8;
			case "Pending":   return 1;
			case "Suspended": return 2;
			case "Cancelled": return 2;
			case "Expired":   return 2;
			default: return 10;
		}
	},

	formatEntitlementPaymentStateColorScheme(state: string | undefined): number {
		switch (state) {
			case "Paid":           return 8;
			case "GracePeriod":    return 1;
			case "PendingPayment": return 1;
			case "Overdue":        return 2;
			case "NotRequired":    return 10;
			default: return 10;
		}
	},

	formatLicenseStatusColorScheme(status: string | undefined): number {
		switch (status) {
			case "Active":    return 8;
			case "Suspended": return 1;
			case "Voided":    return 2;
			case "Unbound":   return 2;
			case "Expired":   return 2;
			case "EmptySeat": return 10;
			default: return 10;
		}
	},

	formatDeploymentStatusColorScheme(status: string | undefined): number {
		switch (status) {
			case "Active":            return 8;
			case "PendingEnrollment": return 1;
			case "Decommissioned":    return 10;
			case "EnrollmentExpired": return 3;
			default: return 10;
		}
	},

	/**
	 * Row-action visibility helpers.
	 *
	 * Why named formatters instead of expression binding `{= ${...} && ${...} === '...' }`?
	 * In `sap.ui.table.Table` cells the formatter inside an expression binding evaluates correctly,
	 * but the resulting boolean does not reach `Button.visible` — the property stays at its
	 * default `true`. Composite binding with a named formatter (this one) propagates reliably.
	 *
	 * All inputs come straight from the row context; treat anything non-string as missing.
	 */
	canMarkOrderPaid(
		canPay: boolean | undefined,
		paymentStatus: string | undefined,
		orderStatus: string | undefined
	): boolean {
		return Boolean(canPay) && paymentStatus !== "Paid" && orderStatus !== "Voided";
	},

	canMarkOrderOverdue(
		canMark: boolean | undefined,
		paymentStatus: string | undefined,
		orderStatus: string | undefined
	): boolean {
		return Boolean(canMark)
			&& paymentStatus === "Unpaid"
			&& orderStatus !== "Voided";
	},

	canVoidLicense(canVoid: boolean | undefined, status: string | undefined): boolean {
		return Boolean(canVoid) && status === "Active";
	},

	canEnrollDeployment(canEnroll: boolean | undefined, status: string | undefined): boolean {
		return Boolean(canEnroll) && status === "PendingEnrollment";
	},

	canReEnrollDeployment(canReEnroll: boolean | undefined, status: string | undefined): boolean {
		return Boolean(canReEnroll) && status === "Active";
	},

	/**
	 * Reset (reaktywacja) deploymentu — ADR-024. Wysokouprzywilejowana operacja recovery,
	 * gated wyłącznie uprawnieniem <c>deployments.reset</c> (rola serwisowa CompuTec).
	 * Domena <c>Deployment.Reset</c> dozwolona z Active/Decommissioned/PendingEnrollment
	 * (idempotentna), więc widoczność nie zawęża się do konkretnego statusu — decyduje samo uprawnienie.
	 */
	canResetDeployment(canReset: boolean | undefined): boolean {
		return Boolean(canReset);
	},

	/**
	 * Visibility of the Advanced actions menu button on the deployment detail: true when at least one
	 * of its items is visible. Built from the same formatters the items use, so the two cannot drift.
	 */
	hasAdvancedDeploymentActions(canReEnroll: boolean | undefined, canEnroll: boolean | undefined,
		canSetOwner: boolean | undefined, canReset: boolean | undefined, status: string | undefined): boolean {
		return formatter.canReEnrollDeployment(canReEnroll, status)      // Re-enroll
			|| formatter.canEnrollDeployment(canEnroll, status)           // Offline enrollment, Offline bundle
			|| formatter.canReEnrollDeployment(canEnroll, status)         // Offline refresh
			|| Boolean(canSetOwner)                                       // Change owner
			|| formatter.canResetDeployment(canReset);                    // Reset, Re-sync
	},

	/**
	 * Tłumaczenie BillingMode na user-friendly string. Parts binding (mode + 4 etykiety i18n).
	 * Wzorzec jak <see cref="formatOrgType"/> — formatter unika łapania bundle z controllera.
	 */
	formatBillingMode(
		mode: string | null | undefined,
		labelPerpetual: string | null | undefined,
		labelPerpetualWithMaintenance: string | null | undefined,
		labelSubscriptionMonthly: string | null | undefined,
		labelSubscriptionAnnual: string | null | undefined
	): string {
		switch (mode) {
			case "Perpetual":                return labelPerpetual ?? mode;
			case "PerpetualWithMaintenance": return labelPerpetualWithMaintenance ?? mode;
			case "SubscriptionMonthly":      return labelSubscriptionMonthly ?? mode;
			case "SubscriptionAnnual":       return labelSubscriptionAnnual ?? mode;
			default: return mode ?? "";
		}
	},

	/** "InPortal" / "OutOfPortal" → user-friendly. Out-of-portal = on-prem (klient sam dystrybuuje). */
	formatLicensingMode(
		mode: string | null | undefined,
		labelInPortal: string | null | undefined,
		labelOutOfPortal: string | null | undefined
	): string {
		switch (mode) {
			case "InPortal":    return labelInPortal ?? mode;
			case "OutOfPortal": return labelOutOfPortal ?? mode;
			default: return mode ?? "";
		}
	},

	/**
	 * ADR-013: "PL (Poland)" / "GLOBAL (Global)" / "" (gdy brak). Używane w DeploymentDetail
	 * basics + jako jednolity render w detal-page'ach Entitlement / License. Code jest stabilny,
	 * Name może się tłumaczyć — UI pokazuje oba.
	 */
	formatLocationCodeWithName(
		code: string | null | undefined,
		name: string | null | undefined
	): string {
		if (!code) {
			return "";
		}
		if (!name) {
			return code;
		}
		return `${code} (${name})`;
	},

	/** Mnożenie packSize × quantity dla kolumny "Total seats" w liniach zamówienia. */
	formatTotalSeats(packSize: number | string | null | undefined, quantity: number | string | null | undefined): string {
		const ps = Number(packSize);
		const qty = Number(quantity);
		if (!Number.isFinite(ps) || ps < 1 || !Number.isFinite(qty) || qty < 1) {
			return "—";
		}
		return String(ps * qty);
	},

	/** "365 days" / "30 days" / "—" (Perpetual nie ma okresu). */
	formatBillingPeriod(days: number | string | null | undefined): string {
		if (days === null || days === undefined || days === "") {
			return "—";
		}
		const n = Number(days);
		if (!Number.isFinite(n) || n < 1) {
			return "—";
		}
		return `${n} d`;
	},

	formatQaCountColorScheme(count: string | undefined): number {
		if (!count || count === "0" || count === "—") return 10;
		return 6;
	},

	/**
	 * Formats a catalog price amount + ISO 4217 currency code via Intl.NumberFormat.
	 * Used by the Pricing matrix (Task 9) for cell display.
	 */
	formatMoney(
		amount: number | string | null | undefined,
		currencyCode: string | null | undefined
	): string {
		if (amount === null || amount === undefined || amount === "") { return "—"; }
		const n = Number(amount);
		if (!Number.isFinite(n)) { return "—"; }
		const lang = Localization.getLanguage();
		if (!currencyCode) { return String(n); }
		return new Intl.NumberFormat(lang, { style: "currency", currency: currencyCode }).format(n);
	}
};

export default formatter;
